import { Cache, DataProxy } from '@apollo/client/cache';
import { InMemoryCache, NormalizedCacheObject, Reference } from '@apollo/client/core';

import { getQueryName, isQueryOperation } from '../helper';

type VarCache = Record<string, Record<string, any>>;

export class CustomInMemoryCache extends InMemoryCache {
  private queryVarCache: VarCache = {};

  override extract(optimistic = false): NormalizedCacheObject {
    const varCache = Object.keys(this.queryVarCache).length ? { VAR_CACHE: this.queryVarCache } : {};
    return { ...super.extract(optimistic), ...varCache };
  }

  override restore(data: NormalizedCacheObject): this {
    const varCache = data?.VAR_CACHE;
    if (varCache) {
      this.queryVarCache = varCache as VarCache;
      delete data.VAR_CACHE;
    }
    return super.restore(data);
  }

  override reset(): Promise<void> {
    this.queryVarCache = {};
    return super.reset();
  }

  override write(write: Cache.WriteOptions): Reference | undefined {
    const { query, variables } = write;
    if (isQueryOperation(query)) {
      this.setQueryVars(getQueryName(query), variables);
    }
    return super.write(write);
  }

  override readQuery<QueryType, TVariables = any>(
    options: DataProxy.Query<TVariables, QueryType>,
    optimistic = false
  ): QueryType | null {
    if (!options.variables) {
      options.variables = this.queryVarCache[getQueryName(options.query)] as TVariables;
    }

    return super.readQuery(options, optimistic);
  }

  override writeQuery<TData = any, TVariables = any>(
    options: DataProxy.WriteQueryOptions<TData, TVariables>
  ): Reference | undefined {
    const queryName = getQueryName(options.query);

    if (!options.variables) {
      options.variables = this.queryVarCache[queryName] as TVariables;
    } else if (!this.queryVarCache[queryName]) {
      // writeQuery isn't used on regular cache writes, hence it should not
      // overwrite cached query vars of previously invoked queries.
      // But it still can add some for queries not present in the cache yet.
      this.setQueryVars(queryName, options.variables);
    }

    return super.writeQuery(options);
  }

  private setQueryVars(operationName: string, variables: Record<string, any>): void {
    if (variables) {
      if (operationName) {
        this.queryVarCache[operationName] = variables;
      } else {
        console.error("Cannot add variables to QueryVar cache as query doesn't have an OperationName!");
      }
    }
  }
}
